import api from "./api";
import { Orcamento, ItemOrcamento, ApiResponse } from "../types";

/** Uma versão do PDF de uma cotação, da mais nova para a mais antiga. */
export interface VersaoPdfCotacao {
  versao: number;
  pdfUrl: string;
  criadoEm?: string | null;
  /** "fornecedor" ou "edicao_interna". */
  origem: string;
  autor: string;
  atual: boolean;
}

export const orcamentoService = {
  /**
   * Lista todos os orçamentos (opcionalmente filtrados por planilhaId)
   */
  async listar(planilhaId?: string): Promise<Orcamento[]> {
    const url = planilhaId
      ? `/orcamentos?planilhaId=${planilhaId}`
      : "/orcamentos";
    const response = await api.get<ApiResponse<Orcamento[]>>(url);

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao listar orçamentos");
    }

    return response.data.data;
  },

  /**
   * Busca orçamento por token (usado pelo fornecedor)
   */
  async buscarPorToken(token: string): Promise<Orcamento> {
    const response = await api.get<ApiResponse<Orcamento>>(
      `/orcamentos/token/${token}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Orçamento não encontrado");
    }

    return response.data.data;
  },

  /**
   * Cria e envia orçamentos para os fornecedores da planilha
   */
  async enviar(
    planilhaId: string
  ): Promise<{ orcamentos: Orcamento[]; total: number }> {
    const response = await api.post<
      ApiResponse<{ orcamentos: Orcamento[]; total: number }>
    >("/orcamentos/enviar", { planilhaId });

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao enviar orçamentos");
    }

    return response.data.data;
  },

  /**
   * Envia orçamentos de forma granular (individual, por item ou por fornecedor)
   */
  async enviarGranular(
    planilhaId: string,
    fornecedores: Array<{
      fornecedor: any;
      itens: ItemOrcamento[];
      email: string;
      telefone?: string;
    }>
  ): Promise<{
    success: boolean;
    message: string;
    totalEnviados: number;
    totalErros: number;
    resultados: Array<{
      fornecedorId?: string;
      fornecedorNome?: string;
      success: boolean;
      itensEnviados?: number;
      linkCotacao?: string;
      orcamentoId?: string;
      reenvio?: boolean;
      message?: string;
    }>;
  }> {
    const response = await api.post<
      ApiResponse<{
        success: boolean;
        message: string;
        totalEnviados: number;
        totalErros: number;
        resultados: any[];
      }>
    >("/orcamentos/enviar-granular", {
      planilhaId,
      fornecedores,
    });

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao enviar orçamentos");
    }

    return response.data.data;
  },

  // O método `responder` foi removido junto com o endpoint
  // PUT /api/orcamentos/token/{token}/responder, que casava o item pelo número
  // da linha — reatribuído por posição a cada mudança na planilha. Nenhuma tela
  // o chamava: quem responde é o portal em /orcamento/{token}, via
  // orcamentoFornecedorService, chaveado pelo Id do item.

  /**
   * Envia um lembrete de 1 clique ao fornecedor de um orçamento ainda aberto (item 3.1).
   * O backend faz rate-limit de 24h (429) e valida o status (409) — a UI apenas reflete.
   */
  async listarVersoesPdf(orcamentoId: string): Promise<VersaoPdfCotacao[]> {
    const response = await api.get<ApiResponse<VersaoPdfCotacao[]>>(
      `/orcamentos/${orcamentoId}/pdfs`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao carregar o histórico de PDFs");
    }
    return response.data.data;
  },

  async lembrarFornecedor(
    orcamentoId: string
  ): Promise<{ ultimoLembreteEm: string; totalLembretes: number }> {
    const response = await api.post<
      ApiResponse<{ ultimoLembreteEm: string; totalLembretes: number }>
    >(`/orcamentos/${orcamentoId}/lembrar`, {});

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao enviar lembrete");
    }

    return response.data.data;
  },

  /**
   * Fornecedor rejeita o orçamento com justificativa
   */
  async rejeitarOrcamento(
    token: string,
    data: { justificativa: string }
  ): Promise<void> {
    const response = await api.post<ApiResponse<void>>(
      `/orcamentos/token/${token}/rejeitar`,
      data
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Erro ao rejeitar orçamento");
    }
  },

  /**
   * Busca orçamento por token (alias para manter compatibilidade)
   */
  async getByToken(token: string): Promise<Orcamento> {
    return this.buscarPorToken(token);
  },

  /**
   * Gera link de acesso para fornecedor
   */
  gerarLink(token: string): string {
    return `${window.location.origin}/orcamento/${token}`;
  },
};

export default orcamentoService;
